import React from "react";
import MarketingShell from "../components/MarketingShell";
import PageMeta from "../components/PageMeta";

/**
 * Privacy Policy — written for an Indian healthcare SaaS. Substitute the
 * placeholders marked [TO FILL] with your legal entity details before going live.
 */
export default function Privacy() {
  return (
    <MarketingShell>
      <PageMeta
        title="Privacy Policy · HeDesk"
        description="How HeDesk collects, uses, stores, and protects your clinic data and the patient data your clinic enters into our platform."
        canonical="https://hedesk.help/privacy"
      />
      <article className="prose-content">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold">Legal</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-slate-400">Last updated: 14 June 2026</p>

        <Lead>
          HeDesk is a clinic-management platform used by doctors, receptionists, and clinic owners across India.
          We take privacy seriously — this page explains, in plain English, what we collect, why we collect it,
          how long we keep it, and your rights.
        </Lead>

        <H2>1. Who we are</H2>
        <P>
          HeDesk is operated by <strong> hemba.</strong>,
          with its registered office at <strong> sabantongba awang leikai,thoubal Manipur -795138</strong>.
          For any privacy-related question or request, write to{" "}
          <a href="mailto:hedesk.app@gmail.com" className="text-blue-400">privacy@hedesk.help</a>.
        </P>

        <H2>2. What we collect</H2>
        <P>HeDesk handles two distinct categories of data:</P>
        <Ul>
          <li><strong>Account data</strong> (about you — the clinic staff): name, email, phone, role, password hash, login timestamps, IP address, browser/device fingerprint.</li>
          <li><strong>Clinic data</strong> (about your patients — entered by you): patient name, age, gender, phone, optional Aadhar (encrypted-in-transit), address, blood group, appointments, prescriptions, invoices, and uploaded files (e.g. lab reports).</li>
        </Ul>
        <P>
          We are a <strong>data processor</strong> for clinic data, and your clinic is the <strong>data controller</strong>.
          We only access clinic data to operate, secure, and improve the service.
        </P>

        <H2>3. Why we collect it</H2>
        <Ul>
          <li>To run your clinic dashboard — appointments, prescriptions, billing, reports.</li>
          <li>To send transactional emails (password resets, invoice receipts) via Resend.</li>
          <li>To process payments through Razorpay and Stripe (we never store full card numbers).</li>
          <li>To detect abuse, prevent fraud, and protect your account (audit logs, rate limits).</li>
          <li>To improve HeDesk through aggregate, anonymised usage metrics.</li>
        </Ul>

        <H2>4. Who we share it with</H2>
        <P>We only share data with third parties strictly necessary to run the service:</P>
        <Ul>
          <li><strong>Razorpay</strong> &amp; <strong>Stripe</strong> — payment processing (PCI-compliant).</li>
          <li><strong>Resend</strong> — transactional email delivery.</li>
          <li><strong>MongoDB Atlas</strong> — primary database hosting (servers in India where available).</li>
          <li><strong>Cloudflare</strong> — DNS, CDN, and DDoS protection.</li>
          <li><strong>Government &amp; legal authorities</strong> — only when compelled by a valid legal order under Indian law.</li>
        </Ul>
        <P>We never sell your clinic data, your patient data, or your staff data to anyone. Ever.</P>

        <H2>5. Where data is stored</H2>
        <P>
          Clinic and patient data is stored in MongoDB Atlas clusters located in <strong>Asia (Mumbai / Singapore)</strong>.
          Backups are retained for 14 days and then permanently destroyed.
        </P>

        <H2>6. How long we keep it</H2>
        <Ul>
          <li><strong>Active accounts</strong>: as long as your subscription is active.</li>
          <li><strong>Cancelled accounts</strong>: 90 days, after which all clinic and patient data is permanently deleted. You can request immediate deletion at any time.</li>
          <li><strong>Audit logs</strong>: 180 days, then permanently deleted.</li>
          <li><strong>Billing records</strong>: 8 years, as required by Indian tax law.</li>
        </Ul>

        <H2>7. Your rights</H2>
        <P>You can, at any time:</P>
        <Ul>
          <li>Access all the data we hold about you.</li>
          <li>Correct inaccurate data via the Settings page.</li>
          <li>Export your full data archive in JSON + CSV (Settings → Export).</li>
          <li>Delete your account &amp; all associated data within 24 hours of a verified request.</li>
          <li>Opt out of all non-essential email communication.</li>
        </Ul>
        <P>
          To exercise any right, email{" "}
          <a href="mailto:hedesk.app@gmail.com" className="text-blue-400">privacy@hedesk.help</a>
          {" "}from the email address on your account.
        </P>

        <H2>8. Security</H2>
        <Ul>
          <li>All traffic is encrypted via TLS 1.2+ (HTTPS).</li>
          <li>Passwords are hashed with bcrypt; we never see or store the plain-text password.</li>
          <li>Role-based access controls separate doctor / receptionist / admin permissions.</li>
          <li>Every public-API call is rate-limited and audit-logged.</li>
          <li>Razorpay signatures are HMAC-verified server-side before a booking is created.</li>
        </Ul>

        <H2>9. Children</H2>
        <P>
          Patient data may include children entered by their parent or guardian via the clinic. HeDesk staff
          accounts must be 18+. We do not knowingly create staff accounts for minors.
        </P>

        <H2>10. Cookies</H2>
        <P>
          HeDesk uses essential cookies for authentication and session security. We do not use third-party
          advertising or behavioural tracking cookies. Analytics, when enabled, is aggregate and anonymised.
        </P>

        <H2>11. Changes to this policy</H2>
        <P>
          If we materially change this policy we will email the primary contact on every active clinic account
          at least 14 days before the change takes effect.
        </P>

        <H2>12. Contact</H2>
        <P>
          <a href="mailto:privacy@hedesk.help" className="text-blue-400">privacy@hedesk.help</a>{" · "}
          <a href="mailto:hello@hedesk.help" className="text-blue-400">hello@hedesk.help</a>
        </P>
      </article>
    </MarketingShell>
  );
}

/* Typography helpers — keeps the prose clean and consistent across legal pages. */
function Lead({ children }) {
  return <p className="mt-8 text-lg text-slate-200 leading-relaxed border-l-2 border-blue-500/40 pl-4">{children}</p>;
}
function H2({ children }) {
  return (
    <h2 className="mt-12 mb-3 text-xl font-semibold text-white tracking-tight" style={{ fontFamily: "Manrope" }}>
      {children}
    </h2>
  );
}
function P({ children }) {
  return <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-4">{children}</p>;
}
function Ul({ children }) {
  return <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-slate-300 leading-relaxed mb-5 marker:text-blue-400">{children}</ul>;
}
